import React, { useState } from 'react';
import { track } from '@vercel/analytics';
import { submitCreatorUpdatesSignup } from '../../services/creatorUpdatesService';
import { Styles as S } from './styles';

interface CreatorUpdatesFormProps {
  source: 'source_links' | 'source_store';
  compact?: boolean;
  showCopy?: boolean;
  showVisual?: boolean;
}

const productBoxImage = '/images/creator-updates/product-box.png';
const guideCoverImage = '/images/creator-updates/guide-cover.png';

export const CreatorUpdatesForm: React.FC<CreatorUpdatesFormProps> = ({
  source,
  compact = false,
  showCopy = true,
  showVisual = false,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const sourcePage = source === 'source_links' ? 'links' : 'store';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');
    track(`${sourcePage}_creator_updates_started`, { source_page: sourcePage });

    try {
      await submitCreatorUpdatesSignup({ email, source });
      setStatus('success');
      setMessage('You are on the list. I will send the project folder and guide there.');
      setEmail('');
      track(`${sourcePage}_creator_updates_completed`, { source_page: sourcePage });
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Could not subscribe right now.');
    }
  };

  return (
    <S.FormCard
      id="creator-updates"
      onSubmit={handleSubmit}
      noValidate
      $compact={compact}
      $hasVisual={showVisual}
      $sourcePage={sourcePage}
    >
      <S.ContentPanel $sourcePage={sourcePage}>
        {showCopy && (
          <S.Copy>
            <S.CopyText $sourcePage={sourcePage}>
              <h2>Creator Updates + Free Gift 🎁</h2>
              <p>Get the project folder I use for client work, plus a guide on how to use it.</p>
            </S.CopyText>
          </S.Copy>
        )}

        <S.FieldGroup $sourcePage={sourcePage}>
          <S.Label htmlFor={`${source}-creator-email`}>Email address</S.Label>
          <S.InputRow $sourcePage={sourcePage}>
            <S.Input
              id={`${source}-creator-email`}
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@studio.com"
              value={email}
              onChange={event => setEmail(event.target.value)}
              required
              aria-describedby={`${source}-creator-message`}
              $sourcePage={sourcePage}
            />
            <S.SubmitButton type="submit" disabled={status === 'loading'} $sourcePage={sourcePage}>
              {status === 'loading' ? 'Sending...' : 'Send it'}
            </S.SubmitButton>
          </S.InputRow>
        </S.FieldGroup>

        <S.Note $sourcePage={sourcePage}>No spam. Unsubscribe any time.</S.Note>

        <S.Message id={`${source}-creator-message`} role={status === 'error' ? 'alert' : 'status'} $status={status}>
          {message}
        </S.Message>
      </S.ContentPanel>

      {showVisual && (
        <S.VisualPanel aria-hidden="true" $sourcePage={sourcePage}>
          <S.VisualGlow />
          <S.GuideCover src={guideCoverImage} alt="" loading="lazy" $sourcePage={sourcePage} />
          <S.ProductBox src={productBoxImage} alt="" loading="lazy" $sourcePage={sourcePage} />
        </S.VisualPanel>
      )}
    </S.FormCard>
  );
};
